import {
  Autocomplete,
  Divider,
  Input,
  Modal,
  ScrollArea,
  TextInput,
  Textarea,
} from '@mantine/core';
import {useEffect, useState} from 'react';
import {MdClose} from 'react-icons/md';
import type {StepCandidateType} from '../../../../../../core/repositories/mypageEntryManagementsRepository';
import {Z_INDEX} from '../../../../constants/zIndex';
import {ModalDateInputContainer} from '../../ModalDateInputContainer';
import type {StepCandidateValidationErrors} from '../../Progress/StepCandidatePreviewModalContainer';
import {InsideModalButton} from '../InsideModalButton';
import {ModalCancelButton} from '../ModalCancelButton';

type Props = {
  opened: boolean;
  onClose: () => void;
  onSave: (selectedCandidate: StepCandidateType, index: number) => void;
  onSaveAll: () => Promise<void>;
  companyName: string;
  companyList: {id: number; value: string}[];
  onCompanyNameChange: (value: string) => void;
  onCompanyNameBlur: () => void;
  onCompanySelect: (companyId: number, companyName: string) => void;
  candidates: StepCandidateType[];
  initialCandidates: StepCandidateType[];
  onCandidateFieldChange: (
    index: number,
    field: keyof StepCandidateType,
    value: string | undefined,
  ) => void;
  savingIndex: number | null;
  isSavingAll: boolean;
  isStepSaved: (index: number) => boolean;
  validationErrors: StepCandidateValidationErrors;
};

// フィールドの順序定義
const FIELD_ORDER: (keyof StepCandidateType)[] = [
  'stepName',
  'deadline',
  'startDate',
  'endDate',
  'place',
  'notes',
];

export const StepCandidatePreviewModal = ({
  opened,
  onClose,
  onSave,
  onSaveAll,
  companyName,
  companyList,
  onCompanyNameChange,
  onCompanyNameBlur,
  onCompanySelect,
  candidates,
  initialCandidates,
  onCandidateFieldChange,
  savingIndex,
  isSavingAll,
  isStepSaved,
  validationErrors,
}: Props) => {
  // 各候補の詳細表示状態を管理
  const [expandedIndexes, setExpandedIndexes] = useState<Set<number>>(
    new Set(),
  );

  // モーダルが閉じられたときに展開状態をリセット
  useEffect(() => {
    if (!opened) {
      setExpandedIndexes(new Set());
    }
  }, [opened]);

  const toggleExpanded = (index: number) => {
    setExpandedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // 指定したフィールドに初期値があるかどうかを判定
  const hasInitialValue = (
    index: number,
    field: keyof StepCandidateType,
  ): boolean => {
    const initial = initialCandidates[index];
    if (!initial) return false;
    const value = initial[field];
    return value != null && value !== '';
  };

  // 初期値があるフィールドとないフィールドを分類
  const getFieldGroups = (index: number) => {
    const withInitialValue = FIELD_ORDER.filter((field) =>
      hasInitialValue(index, field),
    );
    const withoutInitialValue = FIELD_ORDER.filter(
      (field) => !hasInitialValue(index, field),
    );
    return {withInitialValue, withoutInitialValue};
  };

  if (candidates.length === 0) return null;

  const handleSave = (candidate: StepCandidateType, index: number) => {
    onSave(candidate, index);
  };

  const handleSaveAll = async () => {
    await onSaveAll();
  };

  // 未保存の選考予定数を計算
  const unsavedCount = candidates.filter(
    (_, index) => !isStepSaved(index),
  ).length;

  // 保存ボタンの活性/非活性を判定
  const canSaveCandidate = (index: number): boolean => {
    const saved = isStepSaved(index);
    if (saved) return false;

    // 企業名が空の場合は非活性
    if (validationErrors.companyName) return false;

    // ステップ名が空の場合は非活性
    if (validationErrors.candidates?.[index]?.stepName) return false;

    return true;
  };

  // 全て保存ボタンの活性/非活性を判定
  const canSaveAll = (): boolean => {
    if (unsavedCount === 0) return false;
    if (validationErrors.companyName) return false;

    // 未保存の候補の中にステップ名が空のものがあれば非活性
    for (let i = 0; i < candidates.length; i++) {
      if (!isStepSaved(i) && validationErrors.candidates?.[i]?.stepName) {
        return false;
      }
    }

    return true;
  };

  // フィールドをレンダリングする関数
  const renderField = (
    field: keyof StepCandidateType,
    candidate: StepCandidateType,
    index: number,
    saved: boolean,
    stepNameError?: string,
  ) => {
    switch (field) {
      case 'stepName':
        return (
          <div key={field} className="flex flex-col gap-4">
            <Input.Label required>予定</Input.Label>
            <TextInput
              value={candidate.stepName}
              onChange={(e) =>
                onCandidateFieldChange(index, 'stepName', e.target.value)
              }
              placeholder="例: 一次面接"
              error={stepNameError}
              disabled={saved}
              classNames={{
                input: 'rounded-[24px]',
              }}
            />
          </div>
        );
      case 'deadline':
        return (
          <div key={field} className="flex flex-col gap-4">
            <Input.Label>締切</Input.Label>
            <ModalDateInputContainer
              value={candidate.deadline ?? undefined}
              onSave={(value) =>
                onCandidateFieldChange(index, 'deadline', value)
              }
              withTime={true}
              disabled={saved}
            />
          </div>
        );
      case 'startDate':
        return (
          <div key={field} className="flex flex-col gap-4">
            <Input.Label>開始日時</Input.Label>
            <ModalDateInputContainer
              value={candidate.startDate ?? undefined}
              onSave={(value) =>
                onCandidateFieldChange(index, 'startDate', value)
              }
              withTime={true}
              disabled={saved}
            />
          </div>
        );
      case 'endDate':
        return (
          <div key={field} className="flex flex-col gap-4">
            <Input.Label>終了日時</Input.Label>
            <ModalDateInputContainer
              value={candidate.endDate ?? undefined}
              onSave={(value) =>
                onCandidateFieldChange(index, 'endDate', value)
              }
              withTime={true}
              disabled={saved}
            />
          </div>
        );
      case 'place':
        return (
          <div key={field} className="flex flex-col gap-4">
            <Input.Label>場所</Input.Label>
            <div className="relative">
              <TextInput
                value={candidate.place ?? ''}
                onChange={(e) =>
                  onCandidateFieldChange(index, 'place', e.target.value)
                }
                placeholder="例: 本社ビル"
                disabled={saved}
                classNames={{
                  input: 'rounded-[24px]',
                }}
                rightSection={
                  candidate.place && !saved ? (
                    <button
                      type="button"
                      onClick={() =>
                        onCandidateFieldChange(index, 'place', undefined)
                      }
                      className="flex items-center justify-center text-black-3 hover:text-black-1"
                      aria-label="場所をクリア"
                    >
                      <MdClose size={16} />
                    </button>
                  ) : null
                }
              />
            </div>
          </div>
        );
      case 'notes':
        return (
          <div key={field} className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <Input.Label>メモ</Input.Label>
              {candidate.notes && !saved && (
                <button
                  type="button"
                  onClick={() =>
                    onCandidateFieldChange(index, 'notes', undefined)
                  }
                  className="flex items-center gap-4 text-sm text-black-3 hover:text-black-1"
                  aria-label="メモをクリア"
                >
                  <MdClose size={14} />
                  <span>クリア</span>
                </button>
              )}
            </div>
            <Textarea
              value={candidate.notes ?? ''}
              onChange={(e) =>
                onCandidateFieldChange(index, 'notes', e.target.value)
              }
              placeholder="メモを入力"
              minRows={2}
              disabled={saved}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="AI解析結果の確認"
      radius={16}
      centered
      size={'lg'}
      zIndex={Z_INDEX.MODAL}
      styles={{
        title: {
          fontWeight: 'bold',
        },
      }}
    >
      <div className="flex flex-col gap-24">
        {/* 企業名入力 */}
        <div className="flex flex-col gap-4">
          <Input.Label required>企業名</Input.Label>
          <Autocomplete
            value={companyName}
            onChange={onCompanyNameChange}
            onBlur={onCompanyNameBlur}
            onItemSubmit={(item) => {
              onCompanySelect(Number(item.id), item.value);
            }}
            data={companyList}
            placeholder="企業名を入力"
            error={validationErrors.companyName}
            maxDropdownHeight={320}
            radius={24}
            limit={20}
            filter={(value, item) =>
              item.value.toLowerCase().includes(value.toLowerCase().trim())
            }
            withinPortal
          />
        </div>

        <div className="flex flex-col gap-16">
          <div className="text-sm text-black-1">
            保存したい選考予定の内容を確認・編集して保存ボタンを押してください。
          </div>

          {unsavedCount > 0 && (
            <div className="flex items-center justify-between gap-16">
              <div className="text-sm text-black-2" aria-live="polite">
                未保存: {unsavedCount}件
              </div>
              <InsideModalButton
                onClick={handleSaveAll}
                loading={isSavingAll}
                disabled={!canSaveAll() || isSavingAll || savingIndex !== null}
                filled={true}
                aria-label={`選考予定の候補が${unsavedCount}件あります。全て保存する`}
              >
                全て保存
              </InsideModalButton>
            </div>
          )}
        </div>

        <ScrollArea h={500} type="always" className="p-16">
          <div className="flex flex-col">
            {candidates.map((candidate, index) => {
              const saved = isStepSaved(index);
              const saving = savingIndex === index;
              const stepNameError =
                validationErrors.candidates?.[index]?.stepName;
              const stepKey = `step-${index}`;

              const isExpanded = expandedIndexes.has(index);
              const {withInitialValue, withoutInitialValue} =
                getFieldGroups(index);

              return (
                <div key={stepKey}>
                  <div className="relative flex flex-col gap-16 p-16 bg-background-2 rounded-16">
                    {/* 初期値があるフィールド（常に表示） */}
                    {withInitialValue.map((field) =>
                      renderField(
                        field,
                        candidate,
                        index,
                        saved,
                        field === 'stepName' ? stepNameError : undefined,
                      ),
                    )}

                    {/* 「より詳しく」トグル（非表示フィールドがある場合のみ） */}
                    {withoutInitialValue.length > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(index)}
                        className="text-sm text-primary-1 hover:underline text-right"
                        aria-expanded={isExpanded}
                        aria-label={`${candidate.stepName || '選考予定'}の詳細を${isExpanded ? '閉じる' : '開く'}`}
                      >
                        {isExpanded ? '閉じる' : 'より詳しく'}
                      </button>
                    )}

                    {/* 初期値がないフィールド（展開時のみ表示） */}
                    {isExpanded &&
                      withoutInitialValue.map((field) =>
                        renderField(
                          field,
                          candidate,
                          index,
                          saved,
                          field === 'stepName' ? stepNameError : undefined,
                        ),
                      )}

                    {/* 保存ボタン */}
                    <div className="flex justify-end">
                      <InsideModalButton
                        onClick={() => handleSave(candidate, index)}
                        loading={saving}
                        disabled={
                          !canSaveCandidate(index) ||
                          isSavingAll ||
                          (savingIndex !== null && savingIndex !== index)
                        }
                        filled={true}
                        aria-label={`選考予定の候補${candidate.stepName ? `「${candidate.stepName}」` : ''}を保存する`}
                      >
                        {saved ? '保存済み' : '保存'}
                      </InsideModalButton>
                    </div>
                  </div>
                  {index < candidates.length - 1 && (
                    <Divider className="my-16" />
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <div className="flex items-center justify-center">
          <ModalCancelButton onClick={onClose}>閉じる</ModalCancelButton>
        </div>
      </div>
    </Modal>
  );
};
